import React from 'react';
import { yyyymmddToDate } from 'components/features/new-report/daily-report/DailyReport';
import { DATE_FORMAT } from 'components/features/new-report/utils/chartTypes';
import {
  ComparisonBetweenIndexAndAsset as ComparisonBetweenIndexAndAssetInternal,
  ComparisonBetweenIndexAndAssetProps as ComparisonBetweenIndexAndAssetProps_,
  Entries,
  INDEX_TYPE,
  WholeData,
  apiKeyToName,
  createBTCJPY,
  createDJI,
  createMine,
  createNK225,
  createSSI,
  createTOPIX,
  createUSDJPY,
} from '../../components/ComparisonBetweenIndexAndAsset/ComparisonBetweenIndexAndAsset';
import { Chart as Chart_ } from '../../components/ComparisonBetweenIndexAndAsset/HighChart';
import {
  addSignCommaFormat,
  commaFormat,
  getSign,
} from '../../utils/formatter';
import { GetIndexesItemResult, ReportIndexPrice, getIndexes } from 'config/api';
import { useQuery } from 'react-query';
import Loading from 'components/commons/loading/Loading';

export type ComparisonBetweenIndexAndAssetProps = ComparisonBetweenIndexAndAssetProps_;

export const Chart = Chart_;

type IndicesProps = {
  Day: number;
  BTCJPY: ReportIndexPrice | null;
  DJI: ReportIndexPrice | null;
  NK225: ReportIndexPrice | null;
  SSI: ReportIndexPrice | null;
  TOPIX: ReportIndexPrice | null;
  USDJPY: ReportIndexPrice | null;
};

export const ComparisonBetweenIndexAndAsset = (queryPeriod: {
  from: string;
  to: string;
}) => {
  const { data: indexesData, isFetching, isRefetching } = useQuery(
    'weeklyIndexes',
    () => fetchIndexes(queryPeriod.from, queryPeriod.to),
    {
      refetchOnWindowFocus: false,
    }
  );

  if (
    (isFetching && isRefetching) ||
    indexesData === null ||
    indexesData === undefined
  )
    return <LoadingData />;
  const indexes = indexesData as GetIndexesItemResult[];

  const myAssets = indexes.map(i => {
    return i.Mine;
  });
  const latestMyAsset =
    myAssets.length > 0 ? myAssets[myAssets.length - 1] : null;
  const indices = indexes.map(data => {
    return {
      Day: data.Day,
      BTCJPY: data.BTCJPY,
      DJI: data.DJI,
      NK225: data.NK225,
      SSI: data.SSI,
      TOPIX: data.TOPIX,
      USDJPY: data.USDJPY,
    } as IndicesProps;
  });

  // MEMO：
  // 週次レポートは最終日の各指標の値がDBにinsertされる前にレポート作成バッチが動く。
  // GetWeeklyReport.Indexesの最終日もnullとなるため、指標の値を保有している最新日付のデータを変動率・値項目に表示する。
  // チャートは指標の値がnullの日はプロット点とツールチップを非表示にする。

  const arrBTCJPY = indices
    .filter(list => list.BTCJPY !== null)
    .map(list => list.BTCJPY);
  const arrDJI = indices
    .filter(list => list.DJI !== null)
    .map(list => list.DJI);
  const arrNK225 = indices
    .filter(list => list.NK225 !== null)
    .map(list => list.NK225);
  const arrSSI = indices
    .filter(list => list.SSI !== null)
    .map(list => list.SSI);
  const arrTOPIX = indices
    .filter(list => list.TOPIX !== null)
    .map(list => list.TOPIX);
  const arrUSDJPY = indices
    .filter(list => list.USDJPY !== null)
    .map(list => list.USDJPY);

  const latestIndexEntry = [
    ['BTCJPY', arrBTCJPY[arrBTCJPY.length - 1]],
    ['DJI', arrDJI[arrDJI.length - 1]],
    ['NK225', arrNK225[arrNK225.length - 1]],
    ['SSI', arrSSI[arrSSI.length - 1]],
    ['TOPIX', arrTOPIX[arrTOPIX.length - 1]],
    ['USDJPY', arrUSDJPY[arrUSDJPY.length - 1]],
  ] as Entries<Omit<IndicesProps, 'Day'>>;

  const latestIndex = indices[indices.length - 1];
  const latestDay =
    yyyymmddToDate(latestIndex?.Day.toString() || '') || new Date();

  const rowData =
    latestIndexEntry?.map(data => {
      const [key, value] = data;
      const latestDateTime = value?.DateProperty
        ? new Date(value.DateProperty.replace(' ', 'T'))
        : latestDay;
      return {
        indexName: apiKeyToName(key),
        indexType: INDEX_TYPE[key],
        indexSign: getSign(value?.Rate ?? 0),
        id: key,
        indexChangeRatio: addSignCommaFormat((value?.Rate ?? 0) * 100, {
          numberOfDecimals: 2,
        }),
        indexValue: commaFormat(value?.Amount ?? 0, { numberOfDecimals: 2 }),
        indexValueDatetime: `${latestDateTime.getMonth() +
          1}/${latestDateTime.getDate()} ${latestDateTime
          .getHours()
          .toString()
          .padStart(2, '0')}:${latestDateTime
          .getMinutes()
          .toString()
          .padStart(2, '0')}`,
      };
    }) || [];

  const wholeData = indexes.map(datum => {
    const ret: WholeData[number] = [
      yyyymmddToDate(datum.Day.toString()) ?? new Date(),
      datum.Mine?.Incorrect ?? false,
      datum.Mine && datum.Mine.Rate !== null
        ? createMine(datum.Mine.Rate)
        : null,
      datum.NK225 && datum.NK225.Rate !== null
        ? createNK225(datum.NK225.Rate)
        : null,
      datum.TOPIX && datum.TOPIX.Rate !== null
        ? createTOPIX(datum.TOPIX.Rate)
        : null,
      datum.DJI && datum.DJI.Rate !== null ? createDJI(datum.DJI.Rate) : null,
      datum.SSI && datum.SSI.Rate !== null ? createSSI(datum.SSI.Rate) : null,
      datum.USDJPY && datum.USDJPY.Rate !== null
        ? createUSDJPY(datum.USDJPY.Rate)
        : null,
      datum.BTCJPY && datum.BTCJPY.Rate !== null
        ? createBTCJPY(datum.BTCJPY.Rate)
        : null,
    ];
    return ret;
  });

  return (
    <ComparisonBetweenIndexAndAssetInternal
      {...{
        comparisonSign: getSign(latestMyAsset?.Rate ?? 0),
        comparisonPercentage: addSignCommaFormat(
          (latestMyAsset?.Rate ?? 0) * 100,
          {
            numberOfDecimals: 2,
          }
        ),
        valuation: commaFormat(latestMyAsset?.Amount ?? 0),
        latestMyAssetRate: latestMyAsset?.Rate ?? 0,
        rowData,
        wholeData,
        periodUnit: '週間',
        tickInterval: 1,
        dateFormat: DATE_FORMAT.WITH_WEEKDAY,
        Chart,
      }}
    />
  );
};

const LoadingData = () => {
  return (
    <div className="p-4 overflow-hidden">
      <div className="flex items-center mb-3 text-base font-bold text-bluegray-700">
        <img
          src="/static/images/report/icon-chart.svg"
          alt=""
          className="mr-2"
        />
        主要指標と自分の資産を比較
      </div>
      <div className="text-xs text-black">
        あなたの資産と主要指標の1週間の変動率を見比べることができます。
      </div>
      <Loading
        loading={true}
        circularLoading={true}
        style={{
          paddingTop: '60px',
          paddingBottom: '60px',
        }}
      />
    </div>
  );
};

const fetchIndexes = async (from: string, to: string) => {
  const res_ = await getIndexes(from, to);
  const res = res_.Items.map(item => item).reverse();
  return res;
};
